import {Box, Grid, Stack, Typography} from "@mui/material";
import MhdIconMenu from "../../atomicComponents/MhdIconMenu";
import React, {useCallback, useEffect, useState} from "react";
import MhdDatetime from "../../atomicComponents/MhdDatetime";
import MhdInput from "../../atomicComponents/MhdInput";
import {
  corModelFilters,
  runTypeFilters,
  solutionChoices,
  timeOfInterestEventsFilters,
  variableChoices
} from "../../../utils/choices";
import MhdMultiSelect from "../../atomicComponents/MhdMultiSelect";
import Button from "@mui/material/Button";
import {getCorRuns, getCrToDt, getDtToCr, getParamChoices, getScTranges, getStates} from "../../../utils/api";
import dayjs from "dayjs";
import {useTheme} from "@mui/material/styles";
import MhdSelect from "../../atomicComponents/MhdSelect";
import DataFileSelector from "../../atomicComponents/DataFileSelector";

const CurrentSimulation = ({corId, params, paramSetter}) => {
  const theme = useTheme();
  const [cri, setCri] = useState(null)
  const [states, setStates] = useState([])
  const [files, setFiles] = useState({})

  useEffect(() => {
    let corIdNumeric = corId[0];
    if (corIdNumeric) {
      const apiparams = {
        cor_id: corIdNumeric,
        domain: params.domain,
      }
      getStates(apiparams)
        .then(response => {
          setStates(response.data)
        })
    } else {
      setStates([])
    }

  }, [corId, params.domain]);

  useEffect(() => {
    if (!states.length) return;

    // Always work on time-sorted states (ascending)
    const byTime = [...states].sort(
      (a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf()
    );

    let nextStateId: string | number;

    if (params.toi) {
      const target = dayjs(params.toi).valueOf();
      const closest = byTime.reduce(
        (best, s) => {
          const d = Math.abs(dayjs(s.time).valueOf() - target);
          return d < best.diff ? { id: s.id, diff: d } : best;
        },
        { id: byTime[0].id, diff: Infinity }
      );
      nextStateId = closest.id;
    } else {
      // Default to the middle element
      const midIdx = Math.floor((byTime.length - 1) / 2); // lower-middle for even lengths
      nextStateId = byTime[midIdx].id;
    }

    if (params.state !== nextStateId) {
      paramSetter(prev => ({ ...prev, state: nextStateId }));
    }
  }, [states, params.toi, params.state]);

  useEffect(() => {
    let corIdNumeric = corId[0];
    if (corIdNumeric) {
      getParamChoices({cor_id: corIdNumeric})
      .then(response => {
        const fileset = response.data;
        setFiles(fileset)
        paramSetter((prevData) => ({ ...prevData, state: null}) );
      })
    } else {
      setFiles([])
      paramSetter((prevData) => ({ ...prevData, state: null}) );
    }

  }, [corId]);


  const handleStateChange = (v) => {
    const row = states.find(item => item.id === v);
    paramSetter((prevData) => ({ ...prevData, state: v}) );
    dtToCR(row.time)
  }

  const handleDomainChange = (v) => {
    paramSetter((prevData) => ({ ...prevData, domain: v, state: null}) );
  }

  const extractStates = () => {
    if (states) {
      return states.map(row => (
        {
          value: row.id,
          label: `Instantaneous Time: ${dayjs(row.time).format("YYYY-MM-DD HH:mm:ss")}`,
          secondaryLabel: ` [${dayjs(row.state_t0).format("YYYY-MM-DD HH:mm:ss")} – ${dayjs(row.state_t1).format("YYYY-MM-DD HH:mm:ss")}]`,
        })
      );
    }
    return [{ value: '', label: ''}];
  };

  const dtToCR = (v) => {
    paramSetter((prevData) => ({ ...prevData, toi: v}) );
    if (v) {
      getDtToCr({dt: v})
        .then(response => {
          setCri(response.data.toFixed(2))
        })
    } else {
      setCri('')
    }
  }

  const crToDT = (v) => {
    setCri(v)
    if (v) {
      getCrToDt({cr: v})
        .then(response => {
          response.data && paramSetter((prevData) => ({ ...prevData, toi: response.data}) );
        })
    } else {
      paramSetter((prevData) => ({ ...prevData, toi: null}) );
    }
  }

  return (
    <Stack direction={{xs: 'column', sm: 'row'}} width={'100%'} spacing={1}>
      <Box
        width={{xs: '100%', sm: '50%'}}
        sx={{
          p: 2,
          borderRadius: '8px',
          bgcolor: theme.palette.base.rothko5,
          boxShadow: `
            inset 6px 6px 8px rgba(0, 0, 0, 0.3),
            inset -6px -6px 8px rgba(255, 255, 255, 0.5)
          `,
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={3}>
            <MhdSelect
              label="Domain"
              value={params.domain}
              onChange={useCallback((v) => handleDomainChange(v), [])}
              choices={solutionChoices}
            />
          </Grid>
          <Grid item xs={6}>
            <MhdDatetime
              label={"Time of Interest"}
              value={params.toi}
              onChange={useCallback((v) => dtToCR(v), [])}
            />
          </Grid>
          <Grid item xs={3}>
            <MhdInput
              label={"CR of Interest"}
              value={cri}
              onChange={useCallback((v) => crToDT(v), [])}
              units={"CR"}
              clear={true}
            />
          </Grid>
          <Grid item xs={12}>
            <MhdSelect
              label="State of Interest"
              value={params.state}
              onChange={handleStateChange}
              choices={extractStates(states)}
            />
          </Grid>
        </Grid>
      </Box>
      <Box
        width={{xs: '100%', sm: '50%'}}
        sx={{
          p: 2,
          borderRadius: '8px',
          bgcolor: theme.palette.base.rothko5,
          boxShadow: `
            inset 6px 6px 8px rgba(0, 0, 0, 0.3),
            inset -6px -6px 8px rgba(255, 255, 255, 0.5)
          `,
        }}
      >
        <DataFileSelector
          data={(files && corId && params.state && files[params.domain]) ? files[params.domain] : {}}
          paramSetter={paramSetter}
        />
      </Box>

    </Stack>
  );
}

export default CurrentSimulation;